import React, { memo } from 'react';
import styled from 'styled-components';

import { formatCurrency } from 'shared/helpers/utils';
import useProductPrices from 'hooks/use-product-prices';
import useDispensaryFlag from 'shared/hooks/use-dispensary-flag';
import useProductQueryParams from 'hooks/use-product-query-params';
import { DiscountChip, StaffPickChip, ComingSoonChip } from 'components/chips';
import { Typography } from 'src/typography';
import { BadgesContainer, ChipContainer, PriceSection } from './product-card.components';
import { ProductCardProduct } from './product-card.types';

type PriceProps = {
  product: ProductCardProduct;
};

export const Price = memo(
  ({ product }: PriceProps): JSX.Element => {
    const { queryParams } = useProductQueryParams();

    const { standardPrice, specialPrice, discountPercentage } = useProductPrices(product, queryParams.weight);

    const comingSoonProductsFlagEnabled = useDispensaryFlag(
      `rollout.coming-soon-products`,
      'DispensaryID' in product && product.DispensaryID
    );

    const displayPrice = typeof specialPrice === 'number' ? specialPrice : standardPrice;
    const showDiscount = !!discountPercentage && Number.isFinite(discountPercentage) && discountPercentage >= 0;
    const discountDisplay = showDiscount ? `${String(discountPercentage)}% off` : null;
    const showStaffPicksBadge = 'featured' in product && product.featured?.current;

    const isComingSoon = comingSoonProductsFlagEnabled && 'comingSoon' in product && product.comingSoon;

    return (
      <PriceSection>
        <Typography.Label tag='span' size='large'>
          {formatCurrency(displayPrice)}
        </Typography.Label>
        {discountDisplay && (
          <StruckOutPrice tag='span' size='large'>
            {formatCurrency(standardPrice)}
          </StruckOutPrice>
        )}
        {(discountDisplay || showStaffPicksBadge || isComingSoon) && (
          <BadgesContainer>
            {discountDisplay && (
              <ChipContainer mr='5px'>
                <DiscountChip
                  label={
                    <Typography.Label tag='span' size='xsmall' weight='bold'>
                      {discountDisplay}
                    </Typography.Label>
                  }
                />
              </ChipContainer>
            )}
            {showStaffPicksBadge && (
              <ChipContainer mr='5px'>
                <StaffPickChip />
              </ChipContainer>
            )}
            {isComingSoon && (
              <ChipContainer>
                <ComingSoonChip />
              </ChipContainer>
            )}
          </BadgesContainer>
        )}
      </PriceSection>
    );
  }
);

const StruckOutPrice = styled(Typography.Label)`
  color: ${({ theme }) => theme.colors.grey[70]};
  text-decoration: line-through;
  padding-left: 9px;
`;
